import { useEffect, useRef } from "react";
import {
  resolveScannerKey,
  ScannerKeyResolution,
  ScannerKeySource,
} from "./scannerKeyNormalization";
import {
  appendRawScannerEvent,
  createRawScannerEvent,
  describeRawScannerTarget,
  logRawScannerEnvironment,
  logRawScannerTrace,
  RawScannerEvent,
} from "./scannerDiagnostics";
import {
  resolveScannerPayload,
  ScannerPayloadResolution,
} from "./scannerPayloadNormalization";
import {
  hasBarcodeIdentifier,
  hasConsecutiveRapidKeydownCadence,
} from "./scannerTraceClassification";

export { resolveScannerKey } from "./scannerKeyNormalization";

const LEGACY_INTER_CHARACTER_MS = 50;
const LEGACY_MAX_SCAN_MS = 500;
const LEGACY_MIN_LENGTH = 4;
const LEGACY_EDITABLE_SCAN_MS = 100;

export const CASSETTE_SCANNER_TIMING = {
  maxInterCharacterMs: 80,
  quietWindowMs: 100,
  staleResetMs: 1000,
  suffixWindowMs: 250,
} as const;

const PREVIOUS_MAX_INTER_CHARACTER_MS = 60;
const QUIET_WINDOW_SAFETY_MARGIN_MS = 20;
const HONEYWELL_COMPATIBILITY_GAP_MS = 500;
const HONEYWELL_COMPATIBILITY_RESET_MS = 1000;
const UNBUFFERED_TRACE_IDLE_MS = 500;
const UNBUFFERED_TRACE_MINIMUM_RAPID_GAPS = 5;
const ALTERNATE_INPUT_DUPLICATE_WINDOW_MS = 30;
const MAX_COMPATIBILITY_BUFFER_LENGTH = 256;
const SCANNER_CONSOLE_PREFIX = "[BarcodeScanner]";
let scannerDiagnosticSequence = 0;

export const isCompleteCassetteBarcode = (barcode: string) =>
  /^[A-Za-z0-9-]+;[A-Za-z0-9-]+;[A-Za-z0-9-]+$/.test(barcode);

export const isCompleteGrossingBarcode = (barcode: string) =>
  /^[A-Za-z0-9-]+;[A-Za-z0-9-]+(?:;[A-Za-z0-9-]+)?$/.test(barcode);

export const isCompleteDistributionSlideBarcode = (barcode: string) =>
  /^[A-Za-z0-9-]+;[A-Za-z0-9-]+;[A-Za-z0-9-]+;[A-Za-z0-9-]+$/.test(barcode);

type ScannerCallback = (barcode: string) => void | Promise<void>;

type ScannerDispatchSource =
  | "quiet"
  | "enter"
  | "tab"
  | "control"
  | "input"
  | "beforeinput"
  | "deferred";

interface ScannerTimingSummary {
  characterCount: number;
  totalEventDurationMs: number;
  totalHandlerDurationMs: number;
  maxEventGapMs: number;
  maxHandlerGapMs: number;
  cadenceSource: "event" | "handler";
  trustedEvents: boolean;
  fallbackKeyCount: number;
  fallbackSource: ScannerFallbackSource;
}

type ScannerFallbackSource =
  | Exclude<ScannerKeySource, "key">
  | "mixed"
  | "none";

interface PendingScannerDispatch {
  barcode: string;
  source: ScannerDispatchSource;
  target: string;
  timing: ScannerTimingSummary;
}

export interface BarcodeScannerOptions {
  enabled?: boolean;
  ownerKey: string;
  isCompleteBarcode: (barcode: string) => boolean;
  maxInterCharacterMs?: number;
  quietWindowMs?: number;
  staleResetMs?: number;
  suffixWindowMs?: number;
}

interface TextControlSnapshot {
  kind: "text-control";
  target: HTMLInputElement | HTMLTextAreaElement;
  value: string;
  selectionStart: number | null;
  selectionEnd: number | null;
  selectionDirection: "forward" | "backward" | "none" | null;
}

interface ContentEditableSnapshot {
  kind: "content-editable";
  target: HTMLElement;
  html: string;
  range: {
    startPath: number[];
    startOffset: number;
    endPath: number[];
    endOffset: number;
  } | null;
}

type EditableSnapshot = TextControlSnapshot | ContentEditableSnapshot;

interface ScannerCandidate {
  resolution: ScannerPayloadResolution;
  source: "fast" | "compatibility" | "target";
}

type ScannerControlFrame = "prefix" | "enter" | "tab" | null;

const resolveScannerControlFrame = (
  event: KeyboardEvent,
  key: string
): ScannerControlFrame => {
  const normalizedKey = key.toLowerCase();
  if (key === "\u0002") return "prefix";
  if (["\u0003", "\n", "\r"].includes(normalizedKey)) return "enter";
  if (key === "\t") return "tab";
  if (!event.ctrlKey || event.altKey || event.metaKey) return null;
  if (["c", "j", "m"].includes(normalizedKey)) {
    return "enter";
  }
  return normalizedKey === "i" ? "tab" : null;
};

const NON_TERMINATING_CONTROL_KEYS = new Set([
  "Alt",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "Backspace",
  "CapsLock",
  "ContextMenu",
  "Control",
  "Delete",
  "End",
  "Escape",
  "Home",
  "Insert",
  "Meta",
  "NumLock",
  "PageDown",
  "PageUp",
  "Pause",
  "PrintScreen",
  "ScrollLock",
  "Shift",
]);

const canUseUnknownControlTerminator = (event: KeyboardEvent, key: string) =>
  !NON_TERMINATING_CONTROL_KEYS.has(key) &&
  !event.altKey &&
  !event.ctrlKey &&
  !event.metaKey;

const roundTiming = (value: number) => Number(Math.max(0, value).toFixed(1));

const getHandlerTimestamp = () =>
  typeof performance === "undefined" ? Date.now() : performance.now();

const getEventTimestamp = (event: KeyboardEvent) =>
  Number.isFinite(event.timeStamp) ? event.timeStamp : getHandlerTimestamp();

export const resolveScannerCadenceGap = (
  eventGapMs: number,
  handlerGapMs: number,
  isTrusted: boolean
) =>
  isTrusted
    ? { gapMs: eventGapMs, source: "event" as const }
    : { gapMs: handlerGapMs, source: "handler" as const };

export const resolveScannerQuietWindow = (
  maxInterCharacterMs: number,
  configuredQuietWindowMs: number
) =>
  Math.max(
    configuredQuietWindowMs,
    maxInterCharacterMs + QUIET_WINDOW_SAFETY_MARGIN_MS
  );

const getScannerOwnerName = (ownerKey: string) => ownerKey.split(":")[0];

const getScannerTargetName = (target: EventTarget | null) => {
  if (target instanceof HTMLInputElement) return "input";
  if (target instanceof HTMLTextAreaElement) return "textarea";
  if (target instanceof HTMLElement && target.isContentEditable) {
    return "contenteditable";
  }
  if (target instanceof HTMLButtonElement) return "button";
  if (target instanceof HTMLElement) return target.tagName.toLowerCase();
  return "unknown";
};

const getBarcodeShape = (value: string) => {
  const segments = value.split(";");
  return {
    characterCount: value.length,
    delimiterCount: Math.max(0, segments.length - 1),
    segmentLengths: segments.map((segment) => segment.length),
  };
};

const logScannerDiagnostic = (
  event: string,
  ownerKey: string,
  details: Record<string, unknown> = {}
) => {
  scannerDiagnosticSequence += 1;
  // The payload intentionally contains no barcode text, input value, case ID, or URL.
  // eslint-disable-next-line no-console
  console.info(SCANNER_CONSOLE_PREFIX, {
    diagnosticId: `scan-${Date.now().toString(
      36
    )}-${scannerDiagnosticSequence}`,
    event,
    owner: getScannerOwnerName(ownerKey),
    occurredAt: new Date().toISOString(),
    ...details,
  });
};

const mergeFallbackSource = (
  current: ScannerFallbackSource,
  next: Exclude<ScannerKeySource, "key">
): ScannerFallbackSource => {
  if (current === "none" || current === next) return next;
  return "mixed";
};

const logUnresolvedScannerKey = (event: KeyboardEvent, ownerKey: string) => {
  logScannerDiagnostic("scan_key_unresolved", ownerKey, {
    target: getScannerTargetName(event.target),
    trustedEvent: event.isTrusted,
    hasCode: Boolean(event.code),
    hasLegacyKeyCode: Boolean(event.keyCode || event.which),
  });
};

const getNodePath = (root: Node, node: Node) => {
  const path: number[] = [];
  let current: Node | null = node;
  while (current && current !== root) {
    const parent: Node | null = current.parentNode;
    if (!parent) return [];
    path.unshift(Array.from(parent.childNodes).indexOf(current as ChildNode));
    current = parent;
  }
  return path;
};

const getNodeAtPath = (root: Node, path: number[]) =>
  path.reduce<Node | undefined>(
    (node, childIndex) => node?.childNodes[childIndex],
    root
  );

const captureContentEditableRange = (target: HTMLElement) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (
    !target.contains(range.startContainer) ||
    !target.contains(range.endContainer)
  ) {
    return null;
  }
  return {
    startPath: getNodePath(target, range.startContainer),
    startOffset: range.startOffset,
    endPath: getNodePath(target, range.endContainer),
    endOffset: range.endOffset,
  };
};

const isTextControl = (
  target: EventTarget | null
): target is HTMLInputElement | HTMLTextAreaElement =>
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

const isDesignatedBarcodeInput = (
  target: EventTarget | null
): target is HTMLInputElement | HTMLTextAreaElement => {
  if (!isTextControl(target)) return false;
  if (target instanceof HTMLInputElement && target.type === "password") {
    return false;
  }
  if (target.dataset.barcodeScannerInput === "true") return true;
  const identifiers = [target.id, target.name, target.placeholder]
    .join(" ")
    .toLowerCase();
  return identifiers.includes("barcode");
};

const isPasswordTarget = (target: EventTarget | null) =>
  target instanceof HTMLInputElement && target.type === "password";

const getEditableTarget = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof HTMLElement)) return null;
  if (isTextControl(target)) return target;
  return target.isContentEditable
    ? target
    : target.closest("[contenteditable=true]");
};

const captureEditableSnapshot = (
  target: EventTarget | null
): EditableSnapshot | null => {
  const editableTarget = getEditableTarget(target);
  if (!editableTarget) return null;
  if (isTextControl(editableTarget)) {
    return {
      kind: "text-control",
      target: editableTarget,
      value: editableTarget.value,
      selectionStart: editableTarget.selectionStart,
      selectionEnd: editableTarget.selectionEnd,
      selectionDirection: editableTarget.selectionDirection,
    };
  }
  return {
    kind: "content-editable",
    target: editableTarget,
    html: editableTarget.innerHTML,
    range: captureContentEditableRange(editableTarget),
  };
};

const dispatchInput = (target: HTMLElement, data: string | null) => {
  target.dispatchEvent(
    new InputEvent("input", { bubbles: true, inputType: "insertText", data })
  );
};

const restoreTextControl = (snapshot: TextControlSnapshot) => {
  const prototype =
    snapshot.target instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(prototype, "value")?.set?.call(
    snapshot.target,
    snapshot.value
  );
  dispatchInput(snapshot.target, null);
  snapshot.target.setSelectionRange(
    snapshot.selectionStart,
    snapshot.selectionEnd,
    snapshot.selectionDirection ?? undefined
  );
};

const restoreEditableSnapshot = (snapshot: EditableSnapshot | null) => {
  if (!snapshot || !snapshot.target.isConnected) return;
  if (snapshot.kind === "text-control") {
    restoreTextControl(snapshot);
    return;
  }
  snapshot.target.innerHTML = snapshot.html;
  dispatchInput(snapshot.target, null);
  snapshot.target.focus();
  const selection = window.getSelection();
  const range = document.createRange();
  const startNode = snapshot.range
    ? getNodeAtPath(snapshot.target, snapshot.range.startPath)
    : undefined;
  const endNode = snapshot.range
    ? getNodeAtPath(snapshot.target, snapshot.range.endPath)
    : undefined;
  if (startNode && endNode && snapshot.range) {
    range.setStart(startNode, snapshot.range.startOffset);
    range.setEnd(endNode, snapshot.range.endOffset);
  } else {
    range.selectNodeContents(snapshot.target);
    range.collapse(false);
  }
  selection?.removeAllRanges();
  selection?.addRange(range);
};

const insertTextAtSelection = (target: HTMLElement, text: string) => {
  if (!text || !target.isConnected) return;
  if (isTextControl(target)) {
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    target.setRangeText(text, start, end, "end");
    dispatchInput(target, text);
    return;
  }
  target.focus();
  document.execCommand("insertText", false, text);
};

function useLegacyBarcodeScanner(
  onScanRef: React.MutableRefObject<ScannerCallback>,
  enabled: boolean
) {
  const bufferRef = useRef("");
  const firstCharacterTimeRef = useRef(0);
  const lastCharacterTimeRef = useRef(0);
  const fallbackKeyCountRef = useRef(0);
  const fallbackSourceRef = useRef<ScannerFallbackSource>("none");
  const unresolvedKeyLoggedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const resetFallbackDiagnostics = () => {
      fallbackKeyCountRef.current = 0;
      fallbackSourceRef.current = "none";
      unresolvedKeyLoggedRef.current = false;
    };
    const reset = () => {
      bufferRef.current = "";
      firstCharacterTimeRef.current = 0;
      lastCharacterTimeRef.current = 0;
      resetFallbackDiagnostics();
    };
    const recordFallback = (resolution: ScannerKeyResolution) => {
      if (resolution.status !== "resolved" || resolution.source === "key") {
        return;
      }
      fallbackKeyCountRef.current += 1;
      fallbackSourceRef.current = mergeFallbackSource(
        fallbackSourceRef.current,
        resolution.source
      );
    };
    const logFallbackResult = (
      accepted: boolean,
      durationMs: number,
      event: KeyboardEvent
    ) => {
      if (fallbackKeyCountRef.current === 0) return;
      logScannerDiagnostic("scan_legacy_fallback_result", "legacy", {
        accepted,
        durationMs,
        target: getScannerTargetName(event.target),
        characterCount: bufferRef.current.length,
        fallbackKeyCount: fallbackKeyCountRef.current,
        fallbackSource: fallbackSourceRef.current,
      });
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      const resolution = resolveScannerKey(event);
      if (resolution.status === "unresolved") {
        if (!unresolvedKeyLoggedRef.current) {
          logUnresolvedScannerKey(event, "legacy");
          unresolvedKeyLoggedRef.current = true;
        }
        return;
      }
      const { key } = resolution;
      const now = Date.now();
      const editable = Boolean(getEditableTarget(event.target));
      if (key === "Enter" && bufferRef.current.length >= LEGACY_MIN_LENGTH) {
        recordFallback(resolution);
        const duration = now - firstCharacterTimeRef.current;
        if (
          duration <= LEGACY_MAX_SCAN_MS &&
          (!editable || duration <= LEGACY_EDITABLE_SCAN_MS)
        ) {
          event.preventDefault();
          event.stopPropagation();
          const barcode = bufferRef.current;
          logFallbackResult(true, duration, event);
          reset();
          onScanRef.current(barcode);
          return;
        }
        logFallbackResult(false, duration, event);
        reset();
        return;
      }
      if (
        key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        const gap = now - lastCharacterTimeRef.current;
        if (!bufferRef.current || gap <= LEGACY_INTER_CHARACTER_MS) {
          if (!bufferRef.current) {
            resetFallbackDiagnostics();
            firstCharacterTimeRef.current = now;
          }
          recordFallback(resolution);
          bufferRef.current += key;
        } else {
          resetFallbackDiagnostics();
          recordFallback(resolution);
          bufferRef.current = key;
          firstCharacterTimeRef.current = now;
        }
        lastCharacterTimeRef.current = now;
      } else if (!["Shift", "Enter", "CapsLock"].includes(key)) {
        reset();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [enabled, onScanRef]);
}

function useAutomaticBarcodeScanner(
  onScanRef: React.MutableRefObject<ScannerCallback>,
  options: BarcodeScannerOptions
) {
  const bufferRef = useRef("");
  const preventedTextRef = useRef("");
  const lastCharacterTimeRef = useRef(0);
  const firstEventTimeRef = useRef(0);
  const lastEventTimeRef = useRef(0);
  const firstHandlerTimeRef = useRef(0);
  const lastHandlerTimeRef = useRef(0);
  const maxEventGapRef = useRef(0);
  const maxHandlerGapRef = useRef(0);
  const timingCharacterCountRef = useRef(0);
  const lastTargetRef = useRef("unknown");
  const cadenceSourceRef = useRef<"event" | "handler">("handler");
  const trustedEventsRef = useRef(false);
  const fallbackKeyCountRef = useRef(0);
  const fallbackSourceRef = useRef<ScannerFallbackSource>("none");
  const unresolvedKeyLoggedRef = useRef(false);
  const snapshotRef = useRef<EditableSnapshot | null>(null);
  const quietTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const staleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const compatibilityTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const alternateQuietTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const compatibilityBufferRef = useRef("");
  const lastCompatibilityTimeRef = useRef(0);
  const compatibilitySnapshotRef = useRef<EditableSnapshot | null>(null);
  const compatibilityTargetRef = useRef<EventTarget | null>(null);
  const lastPrintableKeyDownRef = useRef<{
    key: string;
    target: EventTarget | null;
    handlerTime: number;
  } | null>(null);
  const rawTraceRef = useRef<RawScannerEvent[]>([]);
  const rawTraceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const restoringEditableRef = useRef(false);
  const inFlightRef = useRef(false);
  const activeBarcodeRef = useRef<string | null>(null);
  const deferredDispatchRef = useRef<PendingScannerDispatch | null>(null);
  const suppressEnterUntilRef = useRef(0);
  const completePredicateRef = useRef(options.isCompleteBarcode);
  completePredicateRef.current = options.isCompleteBarcode;

  const timing = {
    maxInterCharacterMs:
      options.maxInterCharacterMs ??
      CASSETTE_SCANNER_TIMING.maxInterCharacterMs,
    quietWindowMs: resolveScannerQuietWindow(
      options.maxInterCharacterMs ??
        CASSETTE_SCANNER_TIMING.maxInterCharacterMs,
      options.quietWindowMs ?? CASSETTE_SCANNER_TIMING.quietWindowMs
    ),
    staleResetMs: options.staleResetMs ?? CASSETTE_SCANNER_TIMING.staleResetMs,
    suffixWindowMs:
      options.suffixWindowMs ?? CASSETTE_SCANNER_TIMING.suffixWindowMs,
  };

  useEffect(() => {
    let effectActive = true;
    const resolvePayload = (value: string) =>
      resolveScannerPayload(value, completePredicateRef.current);
    const hasStrongScannerFraming = (value: string) => {
      const firstCharacterCode = value.charCodeAt(0);
      const startsWithControlCharacter =
        firstCharacterCode <= 31 || firstCharacterCode === 127;
      return (
        startsWithControlCharacter ||
        /^\][A-Za-z][A-Za-z0-9]/.test(value) ||
        /(?:\[|<)(?:STX|ETX|CR|LF|TAB|HT)(?:\]|>)$/i.test(value)
      );
    };
    const canUseFastResolution = (resolution: ScannerPayloadResolution) =>
      Boolean(resolution.candidate);
    const canUseCompatibilityTarget = (
      target: EventTarget | null,
      raw: string
    ) =>
      !getEditableTarget(target) ||
      isDesignatedBarcodeInput(target) ||
      Boolean(getEditableTarget(target)?.isContentEditable) ||
      hasStrongScannerFraming(raw);
    const canUseIndependentInput = (target: EventTarget | null, raw: string) =>
      isDesignatedBarcodeInput(target) || hasStrongScannerFraming(raw);
    const clearRawTraceTimer = () => {
      if (rawTraceTimerRef.current) {
        clearTimeout(rawTraceTimerRef.current);
        rawTraceTimerRef.current = undefined;
      }
    };
    const flushRawTrace = (
      reason: string,
      rawBuffer: string,
      resolution = resolvePayload(rawBuffer),
      details: Record<string, unknown> = {}
    ) => {
      clearRawTraceTimer();
      const finalTarget =
        compatibilityTargetRef.current ||
        snapshotRef.current?.target ||
        document.activeElement;
      const payload = {
        ownerKey: options.ownerKey,
        reason,
        rawBuffer,
        normalizedCandidate: resolution.candidate,
        transformations: resolution.transformations,
        events: [...rawTraceRef.current],
        details: {
          ...details,
          finalTarget: describeRawScannerTarget(finalTarget, true),
          traceOccurredAt: new Date().toISOString(),
          traceRoute: window.location.href,
        },
      };
      rawTraceRef.current = [];
      if (reason === "dispatch") {
        queueMicrotask(() => logRawScannerTrace(payload));
        return;
      }
      logRawScannerTrace(payload);
    };
    const recordRawEvent = (
      event: Event,
      decision: string,
      bufferBefore: string,
      bufferAfter: string,
      resolution?: ScannerKeyResolution
    ) => {
      appendRawScannerEvent(
        rawTraceRef.current,
        createRawScannerEvent({
          event,
          decision,
          bufferBefore,
          bufferAfter,
          resolution,
        })
      );
      if (!bufferBefore && !bufferAfter) {
        clearRawTraceTimer();
        rawTraceTimerRef.current = setTimeout(() => {
          rawTraceTimerRef.current = undefined;
          const hasRapidKeydownCadence = hasConsecutiveRapidKeydownCadence(
            rawTraceRef.current,
            UNBUFFERED_TRACE_MINIMUM_RAPID_GAPS,
            timing.maxInterCharacterMs
          );
          const hasBarcodeTarget = rawTraceRef.current.some(
            ({ target, activeElement }) =>
              target.barcodeInput === "true" ||
              activeElement.barcodeInput === "true" ||
              [target, activeElement].some((rawTarget) =>
                hasBarcodeIdentifier([
                  rawTarget.barcodeInput,
                  rawTarget.id,
                  rawTarget.name,
                  rawTarget.placeholder,
                ])
              )
          );
          if (hasRapidKeydownCadence || hasBarcodeTarget) {
            flushRawTrace("unbuffered_event_trace_idle", "");
          } else {
            rawTraceRef.current = [];
          }
        }, UNBUFFERED_TRACE_IDLE_MS);
      } else {
        clearRawTraceTimer();
      }
    };
    const clearTimers = () => {
      if (quietTimerRef.current) clearTimeout(quietTimerRef.current);
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
      quietTimerRef.current = undefined;
      staleTimerRef.current = undefined;
    };
    const clearCompatibilityTimers = () => {
      if (compatibilityTimerRef.current) {
        clearTimeout(compatibilityTimerRef.current);
      }
      if (alternateQuietTimerRef.current) {
        clearTimeout(alternateQuietTimerRef.current);
      }
      compatibilityTimerRef.current = undefined;
      alternateQuietTimerRef.current = undefined;
    };
    const resetCompatibility = () => {
      clearCompatibilityTimers();
      compatibilityBufferRef.current = "";
      lastCompatibilityTimeRef.current = 0;
      compatibilitySnapshotRef.current = null;
      compatibilityTargetRef.current = null;
    };
    const resetTiming = () => {
      firstEventTimeRef.current = 0;
      lastEventTimeRef.current = 0;
      firstHandlerTimeRef.current = 0;
      lastHandlerTimeRef.current = 0;
      maxEventGapRef.current = 0;
      maxHandlerGapRef.current = 0;
      timingCharacterCountRef.current = 0;
      lastTargetRef.current = "unknown";
      cadenceSourceRef.current = "handler";
      trustedEventsRef.current = false;
      fallbackKeyCountRef.current = 0;
      fallbackSourceRef.current = "none";
      unresolvedKeyLoggedRef.current = false;
    };
    const getTimingSummary = (): ScannerTimingSummary => ({
      characterCount: timingCharacterCountRef.current,
      totalEventDurationMs: roundTiming(
        lastEventTimeRef.current - firstEventTimeRef.current
      ),
      totalHandlerDurationMs: roundTiming(
        lastHandlerTimeRef.current - firstHandlerTimeRef.current
      ),
      maxEventGapMs: roundTiming(maxEventGapRef.current),
      maxHandlerGapMs: roundTiming(maxHandlerGapRef.current),
      cadenceSource: cadenceSourceRef.current,
      trustedEvents: trustedEventsRef.current,
      fallbackKeyCount: fallbackKeyCountRef.current,
      fallbackSource: fallbackSourceRef.current,
    });
    const recordFallback = (resolution: ScannerKeyResolution) => {
      if (resolution.status !== "resolved" || resolution.source === "key") {
        return;
      }
      fallbackKeyCountRef.current += 1;
      fallbackSourceRef.current = mergeFallbackSource(
        fallbackSourceRef.current,
        resolution.source
      );
    };
    const recordCharacterTiming = (
      eventTime: number,
      handlerTime: number,
      target: EventTarget | null
    ) => {
      if (timingCharacterCountRef.current === 0) {
        firstEventTimeRef.current = eventTime;
        firstHandlerTimeRef.current = handlerTime;
      } else {
        maxEventGapRef.current = Math.max(
          maxEventGapRef.current,
          eventTime - lastEventTimeRef.current
        );
        maxHandlerGapRef.current = Math.max(
          maxHandlerGapRef.current,
          handlerTime - lastHandlerTimeRef.current
        );
      }
      lastEventTimeRef.current = eventTime;
      lastHandlerTimeRef.current = handlerTime;
      timingCharacterCountRef.current += 1;
      lastTargetRef.current = getScannerTargetName(target);
    };
    const reset = (replayPrevented = false) => {
      clearTimers();
      if (replayPrevented && preventedTextRef.current && snapshotRef.current) {
        insertTextAtSelection(
          snapshotRef.current.target,
          preventedTextRef.current
        );
      }
      bufferRef.current = "";
      preventedTextRef.current = "";
      lastCharacterTimeRef.current = 0;
      snapshotRef.current = null;
      resetTiming();
    };
    const resetAll = (replayPrevented = false) => {
      reset(replayPrevented);
      resetCompatibility();
      lastPrintableKeyDownRef.current = null;
    };

    const invokeScannerCallback = (pending: PendingScannerDispatch) => {
      if (!effectActive) return;

      if (inFlightRef.current) {
        if (
          activeBarcodeRef.current === pending.barcode ||
          deferredDispatchRef.current?.barcode === pending.barcode
        ) {
          logScannerDiagnostic("scan_duplicate_ignored", options.ownerKey, {
            source: pending.source,
            target: pending.target,
            barcodeShape: getBarcodeShape(pending.barcode),
            timing: pending.timing,
          });
          return;
        }

        if (!deferredDispatchRef.current) {
          deferredDispatchRef.current = {
            ...pending,
            source: "deferred",
          };
          logScannerDiagnostic("scan_deferred", options.ownerKey, {
            capturedSource: pending.source,
            target: pending.target,
            barcodeShape: getBarcodeShape(pending.barcode),
            timing: pending.timing,
          });
          return;
        }

        logScannerDiagnostic("scan_deferred_overflow", options.ownerKey, {
          source: pending.source,
          target: pending.target,
          barcodeShape: getBarcodeShape(pending.barcode),
          timing: pending.timing,
        });
        return;
      }

      inFlightRef.current = true;
      activeBarcodeRef.current = pending.barcode;
      logScannerDiagnostic("scan_dispatched", options.ownerKey, {
        source: pending.source,
        target: pending.target,
        barcodeShape: getBarcodeShape(pending.barcode),
        timing: pending.timing,
        recoveredAbovePreviousThreshold:
          pending.timing.maxEventGapMs > PREVIOUS_MAX_INTER_CHARACTER_MS,
      });

      const finishDispatch = () => {
        if (!effectActive) return;
        inFlightRef.current = false;
        activeBarcodeRef.current = null;
        const deferred = deferredDispatchRef.current;
        deferredDispatchRef.current = null;
        if (deferred) invokeScannerCallback(deferred);
      };

      try {
        void Promise.resolve(onScanRef.current(pending.barcode)).finally(
          finishDispatch
        );
      } catch (error) {
        logScannerDiagnostic("scan_callback_threw", options.ownerKey, {
          source: pending.source,
          target: pending.target,
          barcodeShape: getBarcodeShape(pending.barcode),
        });
        finishDispatch();
        throw error;
      }
    };

    const dispatchCandidate = (
      resolution: ScannerPayloadResolution,
      source: Exclude<ScannerDispatchSource, "deferred">,
      target: string,
      editableSnapshot: EditableSnapshot | null = null
    ) => {
      if (!resolution.candidate) return;
      const pending: PendingScannerDispatch = {
        barcode: resolution.candidate,
        source,
        target,
        timing: getTimingSummary(),
      };
      if (editableSnapshot) {
        restoringEditableRef.current = true;
        restoreEditableSnapshot(editableSnapshot);
        restoringEditableRef.current = false;
      }
      flushRawTrace(
        "dispatch",
        compatibilityBufferRef.current || bufferRef.current || resolution.raw,
        resolution,
        { source, target }
      );
      resetAll();
      suppressEnterUntilRef.current = Date.now() + timing.suffixWindowMs;
      invokeScannerCallback(pending);
    };
    const dispatch = (source: "quiet" | "enter") => {
      const resolution = resolvePayload(bufferRef.current);
      if (!canUseFastResolution(resolution)) return;
      dispatchCandidate(
        resolution,
        source,
        lastTargetRef.current,
        snapshotRef.current
      );
    };
    const scheduleTimers = () => {
      if (quietTimerRef.current) clearTimeout(quietTimerRef.current);
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
      const resolution = resolvePayload(bufferRef.current);
      if (resolution.candidate && canUseFastResolution(resolution)) {
        quietTimerRef.current = setTimeout(
          () => dispatch("quiet"),
          timing.quietWindowMs
        );
      }
      staleTimerRef.current = setTimeout(() => {
        const rawBuffer = compatibilityBufferRef.current || bufferRef.current;
        logScannerDiagnostic("scan_stale_reset", options.ownerKey, {
          target: lastTargetRef.current,
          barcodeShape: getBarcodeShape(rawBuffer),
          timing: getTimingSummary(),
        });
        reset(true);
      }, timing.staleResetMs);
    };
    const scheduleCompatibilityReset = () => {
      if (compatibilityTimerRef.current) {
        clearTimeout(compatibilityTimerRef.current);
      }
      compatibilityTimerRef.current = setTimeout(() => {
        if (rawTraceRef.current.length > 0) {
          flushRawTrace("compatibility_stale", compatibilityBufferRef.current);
        }
        resetCompatibility();
      }, HONEYWELL_COMPATIBILITY_RESET_MS);
    };
    const appendCompatibilityData = (
      data: string,
      handlerTime: number,
      target: EventTarget | null
    ) => {
      const targetChanged =
        compatibilityTargetRef.current &&
        compatibilityTargetRef.current !== target;
      const startsFramedSession = data.includes("\u0002");
      if (targetChanged || startsFramedSession) resetCompatibility();
      const gap = handlerTime - lastCompatibilityTimeRef.current;
      if (
        compatibilityBufferRef.current &&
        gap > HONEYWELL_COMPATIBILITY_GAP_MS
      ) {
        flushRawTrace(
          "compatibility_gap",
          compatibilityBufferRef.current,
          undefined,
          { gapMs: roundTiming(gap) }
        );
        resetCompatibility();
      }
      if (!compatibilityBufferRef.current) {
        compatibilityTargetRef.current = target;
        compatibilitySnapshotRef.current = captureEditableSnapshot(target);
      }
      compatibilityBufferRef.current = (
        compatibilityBufferRef.current + data
      ).slice(-MAX_COMPATIBILITY_BUFFER_LENGTH);
      lastCompatibilityTimeRef.current = handlerTime;
      lastTargetRef.current = getScannerTargetName(target);
      scheduleCompatibilityReset();
    };
    const beginSession = (
      event: KeyboardEvent,
      key: string,
      timestamps: { eventTime: number; handlerTime: number }
    ) => {
      resetTiming();
      bufferRef.current = key;
      lastCharacterTimeRef.current = timestamps.eventTime;
      snapshotRef.current = captureEditableSnapshot(event.target);
      recordCharacterTiming(
        timestamps.eventTime,
        timestamps.handlerTime,
        event.target
      );
      scheduleTimers();
    };
    const appendCharacter = (
      event: KeyboardEvent,
      key: string,
      timestamps: { eventTime: number; handlerTime: number }
    ) => {
      event.preventDefault();
      event.stopPropagation();
      bufferRef.current += key;
      preventedTextRef.current += key;
      lastCharacterTimeRef.current = timestamps.eventTime;
      recordCharacterTiming(
        timestamps.eventTime,
        timestamps.handlerTime,
        event.target
      );
      scheduleTimers();
    };
    const findSuffixCandidate = (
      event: KeyboardEvent
    ): ScannerCandidate | null => {
      const targetValue = isTextControl(event.target)
        ? event.target.value.trim()
        : "";
      const sources: Array<[ScannerCandidate["source"], string]> = [
        ["fast", bufferRef.current],
        ["compatibility", compatibilityBufferRef.current],
        ["target", targetValue],
      ];
      return (
        sources
          .map(([source, raw]) => ({
            source,
            resolution: resolvePayload(raw),
          }))
          .find(({ source, resolution }) => {
            if (!resolution.candidate) return false;
            if (source === "fast") {
              return canUseFastResolution(resolution);
            }
            if (source === "compatibility") {
              return canUseCompatibilityTarget(event.target, resolution.raw);
            }
            return isDesignatedBarcodeInput(event.target);
          }) || null
      );
    };
    const handleSuffix = (event: KeyboardEvent, key: "Enter" | "Tab") => {
      if (Date.now() <= suppressEnterUntilRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const candidate = findSuffixCandidate(event);
      if (candidate) {
        event.preventDefault();
        event.stopPropagation();
        const source = key === "Enter" ? "enter" : "tab";
        const editableSnapshot =
          candidate.source === "fast"
            ? snapshotRef.current
            : compatibilitySnapshotRef.current;
        dispatchCandidate(
          candidate.resolution,
          source,
          getScannerTargetName(event.target),
          editableSnapshot
        );
        return;
      }
      const targetValue = isTextControl(event.target)
        ? event.target.value.trim()
        : "";
      const rawBuffer =
        targetValue || compatibilityBufferRef.current || bufferRef.current;
      logScannerDiagnostic("scan_manual_fallback", options.ownerKey, {
        target: getScannerTargetName(event.target),
        barcodeShape: getBarcodeShape(rawBuffer),
        bufferedShape: getBarcodeShape(bufferRef.current),
        timing: getTimingSummary(),
        reason: `${key.toLowerCase()}_received_without_complete_candidate`,
      });
      flushRawTrace("incomplete_suffix", rawBuffer, undefined, { key });
      resetAll(true);
    };
    const dispatchControlTerminatedCandidate = (
      event: KeyboardEvent,
      candidate: ScannerCandidate
    ) => {
      event.preventDefault();
      event.stopPropagation();
      const editableSnapshot =
        candidate.source === "fast"
          ? snapshotRef.current
          : compatibilitySnapshotRef.current;
      dispatchCandidate(
        candidate.resolution,
        "control",
        getScannerTargetName(event.target),
        editableSnapshot
      );
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isPasswordTarget(event.target)) {
        resetAll(true);
        return;
      }
      const rawBufferBefore =
        compatibilityBufferRef.current || bufferRef.current;
      const scannerOwnedEvent =
        Boolean(rawBufferBefore) || isDesignatedBarcodeInput(event.target);
      const suspectCompositionKey =
        event.keyCode === 229 || ["Process", "Dead"].includes(event.key);
      if (
        event.isComposing ||
        event.repeat ||
        (suspectCompositionKey && !scannerOwnedEvent)
      ) {
        recordRawEvent(
          event,
          "ignored_composition_or_repeat",
          rawBufferBefore,
          rawBufferBefore
        );
        flushRawTrace("composition_or_repeat_reset", rawBufferBefore);
        resetAll(true);
        return;
      }
      const resolution = resolveScannerKey(event);
      if (resolution.status === "unresolved") {
        const candidate = findSuffixCandidate(event);
        const dispatchesCandidate =
          Boolean(candidate) &&
          canUseUnknownControlTerminator(event, event.key);
        recordRawEvent(
          event,
          dispatchesCandidate
            ? "unresolved_control_terminated_complete_candidate"
            : "unresolved_key",
          rawBufferBefore,
          rawBufferBefore,
          resolution
        );
        if (candidate && dispatchesCandidate) {
          dispatchControlTerminatedCandidate(event, candidate);
          return;
        }
        if (!unresolvedKeyLoggedRef.current) {
          logUnresolvedScannerKey(event, options.ownerKey);
          unresolvedKeyLoggedRef.current = true;
        }
        return;
      }
      const { key } = resolution;
      const controlFrame = resolveScannerControlFrame(event, key);
      if (controlFrame === "prefix") {
        recordRawEvent(
          event,
          "control_frame_prefix",
          rawBufferBefore,
          rawBufferBefore,
          resolution
        );
        resetCompatibility();
        appendCompatibilityData(key, getHandlerTimestamp(), event.target);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (controlFrame && findSuffixCandidate(event)) {
        recordRawEvent(
          event,
          `control_frame_${controlFrame}`,
          rawBufferBefore,
          rawBufferBefore,
          resolution
        );
        handleSuffix(event, controlFrame === "tab" ? "Tab" : "Enter");
        return;
      }
      if (key === "Enter" || key === "Tab") {
        recordFallback(resolution);
        recordRawEvent(
          event,
          `${key.toLowerCase()}_suffix`,
          rawBufferBefore,
          rawBufferBefore,
          resolution
        );
        handleSuffix(event, key);
        return;
      }
      const printable =
        key.length === 1 &&
        !event.metaKey &&
        ((!event.ctrlKey && !event.altKey) ||
          (event.altKey && Boolean(rawBufferBefore)));
      if (!printable) {
        const candidate = findSuffixCandidate(event);
        const dispatchesCandidate =
          Boolean(candidate) && canUseUnknownControlTerminator(event, key);
        const resetsManualEdit =
          Boolean(rawBufferBefore) &&
          NON_TERMINATING_CONTROL_KEYS.has(key) &&
          !["Alt", "Control", "Meta", "Shift"].includes(key);
        recordRawEvent(
          event,
          dispatchesCandidate
            ? "control_terminated_complete_candidate"
            : resetsManualEdit
            ? "manual_control_reset"
            : "ignored_non_printable_during_scanner_session",
          rawBufferBefore,
          rawBufferBefore,
          resolution
        );
        if (candidate && dispatchesCandidate) {
          dispatchControlTerminatedCandidate(event, candidate);
          return;
        }
        if (resetsManualEdit) {
          flushRawTrace("manual_control_reset", rawBufferBefore, undefined, {
            key,
          });
          resetAll(true);
        }
        return;
      }
      const eventTime = getEventTimestamp(event);
      const handlerTime = getHandlerTimestamp();
      const timestamps = { eventTime, handlerTime };
      lastPrintableKeyDownRef.current = {
        key,
        target: event.target,
        handlerTime,
      };
      appendCompatibilityData(key, handlerTime, event.target);
      const compatibilityResolution = resolvePayload(
        compatibilityBufferRef.current
      );
      const hasPrintableTerminator =
        compatibilityResolution.transformations.includes(
          "removed-textual-control-wrapper"
        );
      if (compatibilityResolution.candidate && hasPrintableTerminator) {
        if (alternateQuietTimerRef.current) {
          clearTimeout(alternateQuietTimerRef.current);
        }
        alternateQuietTimerRef.current = setTimeout(() => {
          dispatchCandidate(
            compatibilityResolution,
            "quiet",
            getScannerTargetName(event.target),
            compatibilitySnapshotRef.current
          );
        }, timing.quietWindowMs);
      }
      const eventGap = Math.max(0, eventTime - lastCharacterTimeRef.current);
      const handlerGap = Math.max(0, handlerTime - lastHandlerTimeRef.current);
      // Physical keyboard events carry a trustworthy monotonic event timestamp.
      // Synthetic events use handler cadence so programmatic input cannot claim
      // an artificial near-zero scanner cadence.
      const cadence = resolveScannerCadenceGap(
        eventGap,
        handlerGap,
        event.isTrusted
      );
      const gap = cadence.gapMs;
      cadenceSourceRef.current = cadence.source;
      trustedEventsRef.current = event.isTrusted;
      if (!bufferRef.current || gap > timing.maxInterCharacterMs) {
        reset(true);
        beginSession(event, key, timestamps);
        recordFallback(resolution);
        recordRawEvent(
          event,
          "started_fast_session",
          rawBufferBefore,
          compatibilityBufferRef.current,
          resolution
        );
        return;
      }
      recordFallback(resolution);
      appendCharacter(event, key, timestamps);
      recordRawEvent(
        event,
        "appended_fast_session",
        rawBufferBefore,
        compatibilityBufferRef.current,
        resolution
      );
    };
    const handleBeforeInput = (event: InputEvent) => {
      if (isPasswordTarget(event.target)) {
        resetAll(true);
        return;
      }
      const rawBufferBefore = compatibilityBufferRef.current;
      const data = event.data || "";
      const handlerTime = getHandlerTimestamp();
      const lastKeyDown = lastPrintableKeyDownRef.current;
      const duplicateKeyboardInput =
        data.length === 1 &&
        event.inputType.startsWith("insert") &&
        lastKeyDown?.target === event.target &&
        lastKeyDown.key.toLowerCase() === data.toLowerCase() &&
        handlerTime - lastKeyDown.handlerTime <=
          ALTERNATE_INPUT_DUPLICATE_WINDOW_MS;
      if (duplicateKeyboardInput) {
        lastPrintableKeyDownRef.current = null;
        return;
      }
      recordRawEvent(
        event,
        "beforeinput_observed",
        rawBufferBefore,
        rawBufferBefore
      );
      if (
        !data ||
        !event.inputType.startsWith("insert") ||
        event.inputType.includes("Paste")
      ) {
        return;
      }
      const wholePayload = resolvePayload(data);
      if (
        wholePayload.candidate &&
        canUseIndependentInput(event.target, wholePayload.raw)
      ) {
        event.preventDefault();
        event.stopPropagation();
        dispatchCandidate(
          wholePayload,
          "beforeinput",
          getScannerTargetName(event.target)
        );
        return;
      }
      appendCompatibilityData(data, handlerTime, event.target);
      const bufferedPayload = resolvePayload(compatibilityBufferRef.current);
      if (
        !bufferedPayload.candidate ||
        !canUseIndependentInput(event.target, bufferedPayload.raw)
      ) {
        return;
      }
      if (alternateQuietTimerRef.current) {
        clearTimeout(alternateQuietTimerRef.current);
      }
      alternateQuietTimerRef.current = setTimeout(() => {
        dispatchCandidate(
          bufferedPayload,
          "beforeinput",
          getScannerTargetName(event.target),
          compatibilitySnapshotRef.current
        );
      }, timing.quietWindowMs);
    };
    const handleInput = (event: Event) => {
      if (restoringEditableRef.current) return;
      recordRawEvent(
        event,
        "input_observed",
        compatibilityBufferRef.current,
        compatibilityBufferRef.current
      );
      if (!isDesignatedBarcodeInput(event.target)) {
        return;
      }
      const resolution = resolvePayload(event.target.value);
      if (!resolution.candidate) return;
      dispatchCandidate(
        resolution,
        "input",
        getScannerTargetName(event.target)
      );
    };
    const handlePaste = (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;
      const clipboardText = isDesignatedBarcodeInput(event.target)
        ? clipboardEvent.clipboardData?.getData("text") || ""
        : "";
      recordRawEvent(
        event,
        "paste_observed_without_dispatch",
        compatibilityBufferRef.current,
        compatibilityBufferRef.current
      );
      flushRawTrace("paste_observed", clipboardText);
    };

    if (options.enabled === false) {
      effectActive = false;
      if (options.ownerKey !== "legacy") {
        logScannerDiagnostic("listener_disabled", options.ownerKey, {
          thresholdMs: timing.maxInterCharacterMs,
        });
      }
      resetAll();
      clearRawTraceTimer();
      rawTraceRef.current = [];
      suppressEnterUntilRef.current = 0;
      inFlightRef.current = false;
      activeBarcodeRef.current = null;
      deferredDispatchRef.current = null;
      return undefined;
    }
    logScannerDiagnostic("listener_mounted", options.ownerKey, {
      thresholdMs: timing.maxInterCharacterMs,
      quietWindowMs: timing.quietWindowMs,
      staleResetMs: timing.staleResetMs,
      suffixWindowMs: timing.suffixWindowMs,
    });
    logRawScannerEnvironment(options.ownerKey, {
      thresholdMs: timing.maxInterCharacterMs,
      quietWindowMs: timing.quietWindowMs,
      staleResetMs: timing.staleResetMs,
      suffixWindowMs: timing.suffixWindowMs,
      honeywellCompatibilityGapMs: HONEYWELL_COMPATIBILITY_GAP_MS,
      honeywellCompatibilityResetMs: HONEYWELL_COMPATIBILITY_RESET_MS,
    });
    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("beforeinput", handleBeforeInput, true);
    window.addEventListener("input", handleInput, true);
    window.addEventListener("paste", handlePaste, true);
    return () => {
      if (rawTraceRef.current.length > 0) {
        flushRawTrace(
          "listener_unmounted",
          compatibilityBufferRef.current || bufferRef.current
        );
      }
      logScannerDiagnostic("listener_unmounted", options.ownerKey, {
        hadBufferedCharacters: bufferRef.current.length > 0,
        callbackInFlight: inFlightRef.current,
        deferredScanPresent: Boolean(deferredDispatchRef.current),
      });
      effectActive = false;
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("beforeinput", handleBeforeInput, true);
      window.removeEventListener("input", handleInput, true);
      window.removeEventListener("paste", handlePaste, true);
      resetAll(true);
      clearRawTraceTimer();
      rawTraceRef.current = [];
      suppressEnterUntilRef.current = 0;
      inFlightRef.current = false;
      activeBarcodeRef.current = null;
      deferredDispatchRef.current = null;
    };
  }, [
    onScanRef,
    options.enabled,
    options.ownerKey,
    timing.maxInterCharacterMs,
    timing.quietWindowMs,
    timing.staleResetMs,
    timing.suffixWindowMs,
  ]);
}

export function useBarcodeScanner(
  onScan: ScannerCallback,
  enabledOrOptions: boolean | BarcodeScannerOptions = true
) {
  const onScanRef = useRef(onScan);
  const noopScanRef = useRef<ScannerCallback>(() => undefined);
  onScanRef.current = onScan;
  const automaticOptions =
    typeof enabledOrOptions === "object" ? enabledOrOptions : undefined;
  useLegacyBarcodeScanner(
    onScanRef,
    typeof enabledOrOptions === "boolean" && enabledOrOptions
  );
  useAutomaticBarcodeScanner(
    automaticOptions ? onScanRef : noopScanRef,
    automaticOptions ?? {
      enabled: false,
      ownerKey: "legacy",
      isCompleteBarcode: () => false,
    }
  );
}
